<!--Copyright (c) Laserfiche.
Licensed under the MIT License. See LICENSE in the project root for license information.-->

<p class="documentation-title">Using UI Components from NPM Package in Angular</p>
<p class="documentation-text">This is an alternative to using the CDN that is only possible in Angular.</p>
<h3 id="npm-example">Example: <code>lf-checklist</code></h3>
<p class="documentation-text">
  In the example below, both the implementation and types are provided via
  <code>&#64;laserfiche/lf-ui-components/lf-checklist</code>.
</p>
<ol>
  <li>
    <p class="documentation-text">
      <a (click)="navigateToGettingStarted('npm-usage')">Follow these steps to get started.</a>
    </p>
  </li>
  <li>
    <p class="documentation-text">
      Import the standalone component directly in your component's <code>imports</code> array. No NgModules are needed
      &mdash; all components are standalone and zoneless.
    </p>
    <pre class="code-container">
<span class="code-ts-import">import</span> &#123; LfChecklistComponent &#125; <span class="code-ts-import">from</span> <span class="code-ts-import-source">'&#64;laserfiche/lf-ui-components/lf-checklist'</span>;

&#64;Component(&#123;
  <span class="code-ts-key-name">selector</span>: 'app-attachments',
  <span class="code-ts-key-name">standalone</span>: true,
  <span class="code-ts-key-name">imports</span>: [LfChecklistComponent],
  <span class="code-ts-key-name">templateUrl</span>: './attachments.component.html',
&#125;)
      </pre>
  </li>
  <li>
    <p dir="auto">
      Import the style sheet from the npm package by adding this line in <code>styles.css</code> in your angular
      application.
    </p>

    <pre class="code-container">
<span class="code-ts-import">&#64;import</span> '~&#64;laserfiche/lf-ui-components/cdn/lf-laserfiche-lite.css';
<span class="code-ts-import">&#64;import</span> '~&#64;laserfiche/lf-ui-components/cdn/indigo-pink.css';
      </pre>
  </li>
  <li>
    Use <code>lf-checklist</code>.

    <pre class="code-container">
<span class="code-ts-import">import</span> &#123; AfterViewInit, Component, ViewChild &#125; <span class="code-ts-import">from</span> <span class="code-ts-import-source">'&#64;angular/core'</span>;
<span class="code-ts-comment">// we can directly import types and components from the NPM package</span>
<span class="code-ts-import">import</span> &#123; Checklist, LfChecklistComponent, LfChecklistService &#125; <span class="code-ts-import">from</span> <span class="code-ts-import-source">'&#64;laserfiche/lf-ui-components/lf-checklist'</span><span class="pl-kos">;</span>

&#64;Component(&#123;
  <span class="code-ts-key-name">selector</span>: 'app-attachments',
  <span class="code-ts-key-name">standalone</span>: true,
  <span class="code-ts-key-name">imports</span>: [LfChecklistComponent],
  <span class="code-ts-key-name">templateUrl</span>:  './attachments.component.html',
  <span class="code-ts-key-name">styleUrls</span>: ['./attachments.component.css'],
&#125;)

<span class="code-ts-import">export class</span> ExampleApp <span class="code-ts-import">implements</span> AfterViewInit &#123;

  <span class="code-ts-comment">// allows access to the LfChecklistComponent in the html above, available starting in ngAfterViewInit hook</span>
  &#64;ViewChild('myChecklist') <span class="code-ts-key-name">checklist!</span>: LfChecklistComponent;

  <span class="code-ts-comment">// type Checklist is the type imported from the NPM package</span>
  <span class="code-ts-key-name">currentChecklist: Checklist[];</span>

  <span class="code-ts-comment">// events for the components are received as EventEmitters</span>
  <span class="code-ts-function">onChecklistChanged</span>(checklist: Checklist) &#123;
    <span class="code-ts-comment">// data is passed directly</span>
  &#125;

  <span class="code-ts-function">ngAfterViewInit()</span> &#123;
    <span class="code-ts-comment">// the component's view is initialized after ngAfterViewInit</span>
    this.<span class="code-ts-key-name">checklist</span>.<span class="code-ts-function">initAsync</span>(...)
  &#125;
&#125;
        </pre>
  </li>
</ol>
<h3>Secondary Entry Points</h3>
<p class="documentation-text">
  &#64;laserfiche/lf-ui-components has several secondary entry points, allowing you to only import the components needed
  in your project.
</p>
<ul>
  <li>
    <code class="code"><b>&#64;laserfiche/lf-ui-components/lf-checklist</b></code>
    <span class="documentation-text">
      contains <code>LfChecklistComponent</code> to use <code>&lt;lf-checklist></code>.</span
    >
  </li>
  <li>
    <code><b>&#64;laserfiche/lf-ui-components/lf-login</b></code
    ><span class="documentation-text"> contains <code>LfLoginComponent</code> to use <code>&lt;lf-login></code>.</span>
  </li>
  <li>
    <code><b>&#64;laserfiche/lf-ui-components/lf-metadata</b></code
    ><span class="documentation-text">
      contains <code>LfFieldContainerComponent</code>, <code>LfFieldAdhocContainerComponent</code>, and
      <code>LfFieldTemplateContainerComponent</code> to use <code>&lt;lf-field-container></code>,
      <code>&lt;lf-field-adhoc-container></code>, and <code>&lt;lf-field-template-container></code>.</span
    >
  </li>
  <li>
    <code><b>&#64;laserfiche/lf-ui-components/lf-user-feedback</b></code
    ><span class="documentation-text">
      contains <code>LfUserFeedbackComponent</code> to use <code>&lt;lf-user-feedback></code>.</span
    >
  </li>
  <li>
    <code><b>&#64;laserfiche/lf-ui-components/lf-repository-browser</b></code
    ><span class="documentation-text">
      contains <code>LfRepositoryBrowserComponent</code> to use <code>&lt;lf-repository-browser&gt;</code>.</span
    >
  </li>
</ul>
